import React, { useMemo } from 'react';
import Table from '../../../../../../../components/Table/Table';

const toFilters = (arr) =>
  [...new Set(arr.map((v) => (v != null && v !== '' ? String(v) : '-')))].sort().map((v) => ({ text: v, value: v }));

const getCompanyDisplay = (record) => record.organisationName || '-';
const getPlanDisplay = (record) => record.planName || '-';
const getActiveDateDisplay = (record) => record.activeDate || 'Not activated';
const getExpiryDateDisplay = (record) => record.expiryDate || '-';
const getEmailDisplay = (record) => record.assignedTo || '-';
const getStatusDisplay = (record) => {
  const { status } = record;
  if (status === 'Plan Deleted' || status === 'PlanDeleted') return 'Plan Deleted';
  if (record.isRevoked || status === 'Revoked') return 'Revoked';
  if (record.isExpired || status === 'Expired') return 'Expired';
  return status || 'Active';
};
const getRevokedDisplay = (record) => (record.isRevoked ? 'Yes' : 'No');

export default ({ subscriptions = [] }) => {
  const companyFilters = useMemo(() => toFilters(subscriptions.map(getCompanyDisplay)), [subscriptions]);
  const planFilters = useMemo(() => toFilters(subscriptions.map(getPlanDisplay)), [subscriptions]);
  const activeDateFilters = useMemo(() => toFilters(subscriptions.map(getActiveDateDisplay)), [subscriptions]);
  const expiryDateFilters = useMemo(() => toFilters(subscriptions.map(getExpiryDateDisplay)), [subscriptions]);
  const emailFilters = useMemo(() => toFilters(subscriptions.map(getEmailDisplay)), [subscriptions]);
  const statusFilters = useMemo(() => toFilters(subscriptions.map(getStatusDisplay)), [subscriptions]);
  const revokedFilters = useMemo(() => toFilters(subscriptions.map(getRevokedDisplay)), [subscriptions]);

  const columns = [
    {
      key: 'company',
      title: 'Company',
      dataIndex: 'organisationName',
      filters: companyFilters,
      onFilter: (val, r) => getCompanyDisplay(r) === val,
      sorter: (a, b) => getCompanyDisplay(a).localeCompare(getCompanyDisplay(b)),
      render: (text) => text || '-',
    },
    {
      key: 'planName',
      title: 'Plan Name',
      dataIndex: 'planName',
      filters: planFilters,
      onFilter: (val, r) => getPlanDisplay(r) === val,
      sorter: (a, b) => getPlanDisplay(a).localeCompare(getPlanDisplay(b)),
      render: (text) => text || '-',
    },
    {
      key: 'activeDate',
      title: 'Activate Date',
      dataIndex: 'activeDate',
      sorter: (a, b) => {
        const ta = a.activeDate ? new Date(a.activeDate).getTime() : 0;
        const tb = b.activeDate ? new Date(b.activeDate).getTime() : 0;
        return ta - tb;
      },
      filters: activeDateFilters,
      onFilter: (val, r) => getActiveDateDisplay(r) === val,
      render: (text) => <span>{text || 'Not activated'}</span>,
    },
    {
      key: 'expiryDate',
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      sorter: (a, b) => {
        const ta = a.exactExpiryDate ? new Date(a.exactExpiryDate).getTime() : 0;
        const tb = b.exactExpiryDate ? new Date(b.exactExpiryDate).getTime() : 0;
        return ta - tb;
      },
      filters: expiryDateFilters,
      onFilter: (val, r) => getExpiryDateDisplay(r) === val,
      render: (text) => text || '-',
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'assignedTo',
      filters: emailFilters,
      onFilter: (val, r) => getEmailDisplay(r) === val,
      render: (text) => (text ? <a href={`mailto:${text}`}>{text}</a> : '-'),
    },
    {
      key: 'status',
      title: 'Status',
      filters: statusFilters,
      onFilter: (val, r) => getStatusDisplay(r) === val,
      render: (text, record) => {
        const label = getStatusDisplay(record);
        const mod =
          label === 'Revoked'
            ? 'revoked'
            : label === 'Expired'
              ? 'expired'
              : label === 'Plan Deleted'
                ? 'plan-deleted'
                : 'active';
        return <span className={`bsub-status bsub-status--${mod}`}>{label}</span>;
      },
    },
    {
      key: 'revoked',
      title: 'Revoked',
      filters: revokedFilters,
      onFilter: (val, r) => getRevokedDisplay(r) === val,
      sorter: (a, b) => Number(Boolean(b.isRevoked)) - Number(Boolean(a.isRevoked)),
      render: (text, record) => {
        const label = getRevokedDisplay(record);
        if (record.isRevoked) {
          return <span className="bsub-status bsub-status--revoked">{label}</span>;
        }
        return label;
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={subscriptions}
      rowKey="subscriptionId"
      breakpoint={1024}
      searchFields={['assignedTo', 'organisationName', 'planName', 'activeDate', 'expiryDate', 'status']}
      showExpiredToggle
    />
  );
};
